/**
 * SDK error model.
 *
 * Every rejection from this package is an {@link RtcError}. The `code` values come from the frozen
 * registry in `contracts/idl/v1/errors.json` (`packages/shared/src/errors.ts`); the facade adds the
 * three codes the contract introduces on top of the registry:
 *
 *   RTC_RECONNECTING, RTC_PUBLISH_DENIED, RTC_DEVICE_NOT_FOUND
 *
 * `sdkCode` is the Agora RTC SDK numeric error code (`-101` invalid app id, `-109` token expired,
 * ...). Note that the shared registry stores a few *positive* numbers for entries that map to HTTP
 * statuses, while the Agora client constants are negative; the SDK facade uses the negative client
 * constants because that is the value native apps compare against. The registry is still the source
 * of `code` identity.
 *
 * `retryable` follows the operation semantics rather than the registry's blanket `false`: transport
 * and provider failures are retryable, authorization/validation conflicts are not. This is an
 * intentional, documented resolution of an ambiguity in the registry.
 */
import { ERRORS, SdkErrorCode, isApiError } from '@nimeet/shared/browser';
const FACADE_ERRORS = {
    RTC_RECONNECTING: {
        sdkCode: SdkErrorCode.ERR_CONNECTION_INTERRUPTED,
        message: 'the media connection is reconnecting',
        retryable: true,
    },
    RTC_PUBLISH_DENIED: {
        sdkCode: SdkErrorCode.ERR_NO_PERMISSION,
        message: 'publishing is not permitted for this role or token',
        retryable: false,
    },
    RTC_DEVICE_NOT_FOUND: {
        sdkCode: SdkErrorCode.ERR_NOT_SUPPORTED,
        message: 'no media device is available or permission was denied',
        retryable: false,
    },
};
/**
 * Registry code -> Agora client number. Codes not listed here surface `-1` (ERR_FAILED), matching
 * `packages/shared/src/errors.ts`'s "anything we cannot map stays ERR_FAILED" rule.
 */
const SDK_CODE_BY_CODE = {
    invalid_appid: SdkErrorCode.ERR_INVALID_APP_ID,
    app_disabled: SdkErrorCode.ERR_INVALID_APP_ID,
    invalid_channel_name: SdkErrorCode.ERR_INVALID_CHANNEL_NAME,
    no_server_resources: SdkErrorCode.ERR_NO_SERVER_RESOURCES,
    token_expired: SdkErrorCode.ERR_TOKEN_EXPIRED,
    token_privilege_expired: SdkErrorCode.ERR_TOKEN_EXPIRED,
    invalid_token: SdkErrorCode.ERR_INVALID_TOKEN,
    token_channel_mismatch: SdkErrorCode.ERR_INVALID_TOKEN,
    token_uid_mismatch: SdkErrorCode.ERR_INVALID_TOKEN,
    token_not_yet_valid: SdkErrorCode.ERR_INVALID_TOKEN,
    invalid_credentials: SdkErrorCode.ERR_INVALID_TOKEN,
    missing_authorization: SdkErrorCode.ERR_INVALID_TOKEN,
    join_rejected: SdkErrorCode.ERR_JOIN_CHANNEL_REJECTED,
    banned: SdkErrorCode.ERR_JOIN_CHANNEL_REJECTED,
    rtc_token_denied: SdkErrorCode.ERR_JOIN_CHANNEL_REJECTED,
    already_in_channel: SdkErrorCode.ERR_ALREADY_IN_USE,
    channel_not_found: SdkErrorCode.ERR_NOT_IN_CHANNEL,
    user_not_found: SdkErrorCode.ERR_NOT_IN_CHANNEL,
    unauthorized: SdkErrorCode.ERR_NO_PERMISSION,
    invalid_parameter: SdkErrorCode.ERR_INVALID_ARGUMENT,
    rtc_token_invalid_request: SdkErrorCode.ERR_INVALID_ARGUMENT,
    rtc_uid_mapping_invalid_request: SdkErrorCode.ERR_INVALID_ARGUMENT,
    unsupported_media_type: SdkErrorCode.ERR_NOT_SUPPORTED,
    rtc_provider_unavailable: SdkErrorCode.ERR_CONNECTION_INTERRUPTED,
    upstream_unavailable: SdkErrorCode.ERR_CONNECTION_INTERRUPTED,
    internal_error: SdkErrorCode.ERR_FAILED,
    rtc_provider_disabled: SdkErrorCode.ERR_FAILED,
};
const RETRYABLE_CATEGORIES = new Set(['upstream', 'internal', 'rate_limit']);
function isFacadeErrorCode(code) {
    return (code === 'RTC_RECONNECTING' || code === 'RTC_PUBLISH_DENIED' || code === 'RTC_DEVICE_NOT_FOUND');
}
export function isRtcErrorCode(value) {
    return isFacadeErrorCode(value) || canonicalErrorCode(value) !== null;
}
/** Resolve a registry/aliased code to its canonical registry key (case-insensitive for RTC_*). */
export function canonicalErrorCode(code) {
    if (Object.prototype.hasOwnProperty.call(ERRORS, code))
        return code;
    const upper = code.toUpperCase();
    for (const [key, definition] of Object.entries(ERRORS)) {
        if (definition.code.toUpperCase() === upper || key.toUpperCase() === upper)
            return key;
    }
    return null;
}
function resolveRtcErrorCode(code) {
    if (isFacadeErrorCode(code))
        return code;
    const canonical = canonicalErrorCode(code);
    return canonical ? ERRORS[canonical].code : 'internal_error';
}
function defaultSdkCode(code) {
    if (isFacadeErrorCode(code))
        return FACADE_ERRORS[code].sdkCode;
    const canonical = canonicalErrorCode(code);
    return canonical
        ? (SDK_CODE_BY_CODE[canonical] ?? SdkErrorCode.ERR_FAILED)
        : SdkErrorCode.ERR_FAILED;
}
function defaultMessage(code) {
    if (isFacadeErrorCode(code))
        return FACADE_ERRORS[code].message;
    const canonical = canonicalErrorCode(code);
    return canonical ? ERRORS[canonical].message : ERRORS.internal_error.message;
}
function defaultRetryable(code) {
    if (isFacadeErrorCode(code))
        return FACADE_ERRORS[code].retryable;
    const canonical = canonicalErrorCode(code);
    return canonical ? RETRYABLE_CATEGORIES.has(ERRORS[canonical].category) : true;
}
/** Single error type across the facade. */
export class RtcError extends Error {
    code;
    sdkCode;
    retryable;
    details;
    /** Original rejection, retained for diagnostics; never part of the public error table. */
    originalCause;
    constructor(code, message, options = {}) {
        const resolved = resolveRtcErrorCode(code);
        super(message ?? defaultMessage(resolved));
        this.name = 'RtcError';
        this.code = resolved;
        this.sdkCode = options.sdkCode ?? defaultSdkCode(resolved);
        this.retryable = options.retryable ?? defaultRetryable(resolved);
        if (options.details)
            this.details = options.details;
        if (options.cause !== undefined)
            this.originalCause = options.cause;
    }
    toJSON() {
        const body = {
            name: 'RtcError',
            code: this.code,
            sdkCode: this.sdkCode,
            message: this.message,
            retryable: this.retryable,
        };
        if (this.details)
            body.details = this.details;
        return body;
    }
}
export function isRtcError(value) {
    return value instanceof RtcError;
}
/** Construct an `RtcError` with the registry's defaults unless overridden. */
export function rtcError(code, message, options = {}) {
    return new RtcError(code, message, options);
}
/**
 * 状态码 → 错误码的**兜底**映射（仅在响应体没有 `code` 时使用）。
 *
 * ADR-0013 之后：401 = 「我不知道你是谁」（认证失败，如会话过期/令牌无效），
 * 403 = 「我知道你是谁，但你不能做这件事」（`banned`/`service_not_enabled`/权限不足）。
 * 之前这里把 401 映射成 `invalid_credentials`、403 映射成 `unauthorized`——正好反了，
 * 而且 403 的 `unauthorized` 会让客户端把「权限不足」误判成「需要重新登录」。
 */
const STATUS_TO_CODE = {
    400: 'invalid_parameter',
    401: 'unauthorized',
    403: 'banned',
    404: 'channel_not_found',
    409: 'already_in_channel',
    415: 'unsupported_media_type',
    429: 'rate_limited',
    502: 'upstream_unavailable',
    503: 'upstream_unavailable',
};
function pickMessage(body) {
    if (typeof body !== 'object' || body === null)
        return undefined;
    const record = body;
    if (typeof record.message === 'string' && record.message.length > 0)
        return record.message;
    if (typeof record.reason === 'string' && record.reason.length > 0)
        return record.reason;
    if (typeof record.error === 'string' && record.error.length > 0)
        return record.error;
    return undefined;
}
function pickServerCode(body) {
    if (typeof body !== 'object' || body === null)
        return undefined;
    const record = body;
    return typeof record.code === 'string' && record.code.length > 0 ? record.code : undefined;
}
/**
 * Normalize an HTTP/API failure into `RtcError`. Server `code` wins; unknown codes degrade by status.
 */
export function normalizeHttpFailure(status, body, fallbackMessage) {
    const serverCode = pickServerCode(body);
    const canonical = serverCode ? canonicalErrorCode(serverCode) : null;
    const message = pickMessage(body) ?? fallbackMessage ?? `HTTP ${status}`;
    if (canonical) {
        const error = new RtcError(canonical, message);
        // Authentication/validation failures are terminal; keep status out of the public surface.
        return error;
    }
    const code = STATUS_TO_CODE[status] ?? 'internal_error';
    return new RtcError(code, message, { details: { status } });
}
/** Normalize an Agora-style server code without an HTTP status. */
export function normalizeServerCode(code, message) {
    const canonical = canonicalErrorCode(code);
    if (canonical)
        return new RtcError(canonical, message);
    if (isFacadeErrorCode(code.toUpperCase())) {
        return new RtcError(code.toUpperCase(), message);
    }
    return new RtcError('internal_error', message ?? `unknown server error code: ${code}`, {
        details: { serverCode: code },
    });
}
function classifyMessage(message) {
    const text = message.toLowerCase();
    if (text.includes('permission') || text.includes('not allowed') || text.includes('denied')) {
        return 'RTC_PUBLISH_DENIED';
    }
    if (text.includes('device') ||
        text.includes('camera') ||
        text.includes('microphone') ||
        text.includes('getusermedia') ||
        text.includes('notfound')) {
        return 'RTC_DEVICE_NOT_FOUND';
    }
    if (text.includes('expired')) {
        if (text.includes('privilege'))
            return 'token_privilege_expired';
        return 'token_expired';
    }
    if (text.includes('token'))
        return 'invalid_token';
    if (text.includes('appid') || text.includes('app id'))
        return 'invalid_appid';
    if (text.includes('channel'))
        return 'invalid_channel_name';
    if (text.includes('disconnect') || text.includes('connection') || text.includes('network')) {
        return 'upstream_unavailable';
    }
    return 'internal_error';
}
function classifyDomException(error) {
    if (typeof error !== 'object' || error === null)
        return null;
    const record = error;
    if (typeof record.name !== 'string')
        return null;
    const message = typeof record.message === 'string' ? record.message : record.name;
    switch (record.name) {
        case 'NotAllowedError':
        case 'NotFoundError':
        case 'NotReadableError':
        case 'OverconstrainedError':
        case 'SecurityError':
            return new RtcError('RTC_DEVICE_NOT_FOUND', message, { cause: error });
        case 'AbortError':
            return new RtcError('upstream_unavailable', message, { cause: error });
        default:
            return null;
    }
}
/**
 * Normalize any rejection (RtcError, ApiError, DOMException, response-shaped object, thrown string)
 * into an `RtcError`. `fallback` is used when no specific mapping is possible.
 */
export function normalizeError(error, fallback = 'internal_error') {
    if (error instanceof RtcError)
        return error;
    if (isApiError(error)) {
        const apiError = error;
        const canonical = canonicalErrorCode(apiError.code);
        return new RtcError(canonical ?? fallback, apiError.message, { cause: error });
    }
    const domError = classifyDomException(error);
    if (domError)
        return domError;
    if (typeof error === 'object' && error !== null) {
        const record = error;
        if (typeof record.code === 'string') {
            const canonical = canonicalErrorCode(record.code);
            if (canonical) {
                return new RtcError(canonical, pickMessage(error), { cause: error });
            }
            if (isFacadeErrorCode(record.code)) {
                return new RtcError(record.code, pickMessage(error), { cause: error });
            }
        }
        if (record.error instanceof RtcError)
            return record.error;
    }
    if (typeof error === 'string') {
        return new RtcError(classifyMessage(error), error);
    }
    if (error instanceof Error) {
        return new RtcError(classifyMessage(error.message), error.message, { cause: error });
    }
    return new RtcError(fallback, undefined, { cause: error });
}
//# sourceMappingURL=errors.js.map